
// ABOUTME: Enhanced list item component that displays detailed workout summaries and meal portions
// ABOUTME: Provides rich, structured views for fitness and nutrition data with exercise lists and meal breakdowns

import React from 'react';
import { DataItem, WorkoutData, MealData, ProfileData, WorkoutTemplateData, MealTemplateData } from '../types';

interface ListItemProps {
    item: DataItem;
    onEdit?: (item: DataItem) => void;
    onDelete?: (item: DataItem) => void;
}

const ListItem: React.FC<ListItemProps> = ({ item, onEdit, onDelete }) => {
    const formatDate = (timestamp: number) => {
        const date = new Date(timestamp);
        const dateStr = date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
        const timeStr = date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
        return `${dateStr} ${timeStr}`;
    };

    const renderWorkoutDetails = (data: WorkoutData) => (
        <div className="mt-2 space-y-2">
            {/* Workout Stats - Removed */}

            {/* Exercise List */}
            <div>
                <h4 className="text-xs font-semibold text-slate-300 mb-1">Exercises</h4>
                <div className="overflow-x-auto pb-1">
                    <div className="flex gap-2 min-w-max">
                        {data.exercises.slice().reverse().map((exercise, index) => (
                            <div key={index} className="flex-shrink-0 min-w-[140px]">
                                <div className="text-xs text-slate-300 font-medium mb-0.5 truncate">
                                    {exercise.name}
                                    {exercise.setNumber && <span className="text-slate-500 ml-1">#{exercise.setNumber}</span>}
                                </div>
                                <div className="flex flex-wrap gap-1 text-xs text-slate-400">
                                    {exercise.reps && (
                                        <span className="text-xs">{exercise.reps} reps</span>
                                    )}
                                    {exercise.weight && (
                                        <span className="text-xs">{exercise.weight}lbs</span>
                                    )}
                                    {exercise.duration && (
                                        <span className="text-xs">{Math.floor(exercise.duration / 60)}:{(exercise.duration % 60).toString().padStart(2, '0')}</span>
                                    )}
                                    {exercise.distance && (
                                        <span className="text-xs">{exercise.distance}mi</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );

    const renderMealDetails = (data: MealData) => (
        <div className="mt-2 space-y-2">
            {/* All Meal Labels */}
            <div className="overflow-x-auto pb-1">
                <div className="flex gap-2 text-xs min-w-max">
                    <span className="px-2 py-1 bg-green-600/20 text-green-300  text-xs font-medium capitalize">
                        {data.mealType}
                    </span>
                    <span className="px-2 py-1 bg-slate-600/30 text-slate-300  text-xs font-medium">
                        {data.prepTime} min prep
                    </span>
                    <span className="px-2 py-1 bg-slate-600/30 text-slate-300  text-xs font-medium">
                        {data.calories} cal
                    </span>
                    <span className="px-2 py-1 bg-slate-600/30 text-slate-300  text-xs font-medium">
                        {data.protein}g protein
                    </span>
                    <span className="px-2 py-1 bg-slate-600/30 text-slate-300  text-xs font-medium">
                        {data.carbs}g carbs
                    </span>
                    <span className="px-2 py-1 bg-slate-600/30 text-slate-300  text-xs font-medium">
                        {data.fat}g fat
                    </span>
                    {data.fiber && (
                        <span className="px-2 py-1 bg-slate-600/30 text-slate-300  text-xs font-medium">
                            {data.fiber}g fiber
                        </span>
                    )}
                </div>
            </div>

            {/* Portions */}
            <div>
                <h4 className="text-xs font-semibold text-slate-300 mb-1">Portions</h4>
                <div className="overflow-x-auto pb-1">
                    <div className="flex gap-3 min-w-max">
                        {data.portions.slice().reverse().map((portion, index) => (
                            <div key={index} className="flex-shrink-0 min-w-[120px]">
                                <div className="text-xs text-slate-300 font-medium mb-0.5 truncate">{portion.name}</div>
                                <div className="text-xs text-slate-400">{portion.calories} cal</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );

    const renderProfileDetails = (data: ProfileData) => (
        <div className="mt-2 space-y-2">
            {/* Profile Stats */}
            <div className="flex flex-wrap gap-2 text-xs">
                <span className="px-1.5 py-0.5 bg-slate-600/20 text-slate-300 text-xs font-medium">
                    Age: {data.age}
                </span>
                <span className="px-1.5 py-0.5 bg-slate-600/20 text-slate-300 text-xs font-medium">
                    {data.weight} lbs
                </span>
                <span className="px-1.5 py-0.5 bg-slate-600/20 text-slate-300 text-xs font-medium">
                    {data.targetCalories} cal/day
                </span>
                <span className="px-1.5 py-0.5 bg-slate-600/20 text-slate-300 text-xs font-medium">
                    {data.activityLevel}
                </span>
            </div>
            {/* Goals */}
            <div className="text-xs text-slate-400">
                Goals: {data.goals.join(', ')}
            </div>
        </div>
    );

    return (
        <article className="bg-slate-800/50 backdrop-blur-md shadow-xl p-2 ring-1 ring-slate-700 transition-all duration-300 ease-in-out cursor-pointer">
            <div className="flex flex-col">
                <div className="w-full">
                    {/* Header */}
                    <div className="mb-2">
                        <div className="flex items-center justify-between gap-2 mb-1">
                            <h3 className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">
                                {item.name}
                            </h3>
                            {/* Action Buttons */}
                            {(onEdit || onDelete) && (
                                <div className="flex gap-1">
                                    {onEdit && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onEdit(item);
                                            }}
                                            className="p-1 text-slate-400 hover:text-blue-400 hover:bg-blue-400/10 transition-colors"
                                            title="Edit item"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                        </button>
                                    )}
                                    {onDelete && item.category !== 'profile' && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDelete(item);
                                            }}
                                            className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-400/10 -md transition-colors"
                                            title="Delete item"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                        {/* Category and Date Tags */}
                        <div className="flex gap-3 text-xs">
                            <span className={`font-medium capitalize ${
                                item.category === 'workout' ? 'text-blue-400' :
                                item.category === 'meal' ? 'text-green-400' :
                                item.category === 'workout_template' ? 'text-blue-300' :
                                item.category === 'meal_template' ? 'text-green-300' :
                                'text-purple-400'
                            }`}>
                                {item.category.replace('_', ' ')}
                            </span>
                            <span className="text-slate-400">
                                {formatDate(item.timestamp)}
                            </span>
                        </div>
                    </div>
                    {item.data && item.category === 'workout' && renderWorkoutDetails(item.data as WorkoutData)}
                    {item.data && item.category === 'meal' && renderMealDetails(item.data as MealData)}
                    {item.data && item.category === 'profile' && renderProfileDetails(item.data as ProfileData)}
                    {item.data && item.category === 'workout_template' && renderWorkoutDetails(item.data as WorkoutTemplateData)}
                    {item.data && item.category === 'meal_template' && renderMealDetails(item.data as MealTemplateData)}
                </div>
            </div>
        </article>
    );
};

export default ListItem;
